import { useSelector } from 'react-redux';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  CssBaseline,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { useThemes } from '../../../../components/Theme/theme';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useReportContext } from '../ReportContext';
import { useState, useEffect, useRef } from 'react';
import { showWarningSnackbar } from '../../../../components/snackBar/snakbar';

const HeaderListPage = ({ dat }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchCashBookText, setSearchCashBookText] = useState('');
  const { id } = useParams();
  const openPopover = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    // debugger;
    if (id === '4') {
      setAnchorEl(null);
    }
  }, [id]);

  // Clear selected accounts when report ID changes
  const previousReportIdRef = useRef(id);
  useEffect(() => {
    if (previousReportIdRef.current !== id && previousReportIdRef.current !== null) {
      // Report ID changed, clear selected accounts
      setSelectedAccounts([]);
    }
    previousReportIdRef.current = id;
  }, [id]);

  // Read selected accounts from URL on mount and when URL changes
  useEffect(() => {
    const idsParam = searchParams.get('ids');
    if (idsParam) {
      // Decode and split the comma-separated IDs
      const decodedIds = decodeURIComponent(idsParam);
      const idsArray = decodedIds
        .split(',')
        .filter(Boolean)
        .map((id) => id.trim());
      setSelectedAccounts(idsArray);
    } else {
      setSelectedAccounts([]);
    }
  }, [searchParams]);

  // Update URL when selected accounts change
  const updateUrlWithAccounts = (accountIds) => {
    const newParams = new URLSearchParams(searchParams);

    if (accountIds.length > 0) {
      // Store as comma-separated string: ids=10,20,30
      // Manually set the ids parameter to avoid URLSearchParams encoding commas as %2C
      const idsString = accountIds.join(',');

      // Remove old ids param first
      newParams.delete('ids');

      // Manually construct the query string to avoid comma encoding
      const queryParts = [];
      newParams.forEach((value, key) => {
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      });
      // Add ids parameter without encoding commas
      queryParts.push(`ids=${idsString}`);

      // Use navigate to set the URL with manually constructed query string
      const currentPath = window.location.pathname;
      navigate(`${currentPath}?${queryParts.join('&')}`, { replace: true });
    } else {
      // Remove ids param if no accounts selected
      newParams.delete('ids');
      setSearchParams(newParams, { replace: true });
    }
  };

  // Handle checkbox selection
  const handleCheckboxChange = (accountId) => {
    // Convert accountId to string for consistent comparison
    const accountIdStr = String(accountId);
    const isSelected = selectedAccounts.some((id) => String(id) === accountIdStr);
    const isTab7 = Number(id) === 7;
    const isTab9 = Number(id) === 9;

    if (isSelected) {
      // Remove the account
      const updatedAccounts = selectedAccounts.filter((id) => String(id) !== accountIdStr);
      setSelectedAccounts(updatedAccounts);
      updateUrlWithAccounts(updatedAccounts);
    } else {
      // For tab 7 and tab 9, only allow one account selection
      if (isTab7 || isTab9) {
        // Replace all selected accounts with the new one (single selection)
        setSelectedAccounts([accountIdStr]);
        updateUrlWithAccounts([accountIdStr]);
      } else {
        // For other tabs, allow up to 4 accounts
        if (selectedAccounts.length < 4) {
          const updatedAccounts = [...selectedAccounts, accountIdStr];
          setSelectedAccounts(updatedAccounts);
          updateUrlWithAccounts(updatedAccounts);
        } else {
          showWarningSnackbar('Cannot select more than 4 accounts');
        }
      }
    }
  };

  // Handle row click - select only the clicked account (replace all others)
  const handleRowClick = (accountId) => {
    const accountIdStr = String(accountId);
    // Set only this account in URL (replacing all others)
    setSelectedAccounts([accountIdStr]);
    updateUrlWithAccounts([accountIdStr]);
    setAnchorEl(null);
  };

  const { theme } = useThemes();
  let getCompany = useSelector((state) => state?.companyReducer?.companyDetails);
  const navigate = useNavigate();
  const { reportData, loading } = useReportContext();
  const ledgerData =
    searchCashBookText !== ''
      ? reportData.allAccounts.filter(
          (fil) => fil.accountname?.toLowerCase().includes(searchCashBookText.toLowerCase()) // Correct filter logic
        )
      : reportData?.allAccounts;

  return (
    <Box
      sx={{
        display: getCompany.relatedcompanydetailsnew[0].registrationtype === 1 && dat.id === 6 ? 'none' : 'flex',
        justifyContent: 'space-evenly',
        color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
      }}
      onClick={(event, newValue) => {
        const targetPath = `/reports/${dat.id}`;
        const currentPath = window.location.pathname;

        // Only navigate if the path is actually changing (don't navigate if already on same tab)
        if (currentPath !== targetPath) {
          // When report ID changes, clear the ids parameter but preserve other params
          const currentSearchParams = new URLSearchParams(searchParams);
          currentSearchParams.delete('ids'); // Remove selected account IDs when switching reports
          const queryString = currentSearchParams.toString();
          const newUrl = queryString ? `${targetPath}?${queryString}` : targetPath;
          navigate(newUrl, { replace: true });
        }
        // Always open/close the popover
        setAnchorEl(event.currentTarget);
      }}
    >
      <Typography
        variant="h1"
        key={dat.id}
        sx={
          dat.id === Number(id)
            ? {
                textDecoration: 'underline',
                textDecorationColor: theme.palette.primary.main,
                textDecorationThickness: '3px',
                textUnderlineOffset: '10px',
                cursor: 'pointer',
              }
            : {
                cursor: 'pointer',
              }
        }
      >
        {dat.label}
      </Typography>
      {[1, 2, 3, 5, 6, 7, 9].includes(dat.id) ? <ArrowDropDownIcon /> : null}

      <Popover
        id="tree-popoverggg"
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        onClick={(e) => {
          // Prevent closing when clicking inside the popover
          e.stopPropagation();
        }}
        sx={{
          marginTop: '10px',
          '& .MuiPaper-root': {
            maxHeight: '70vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            background: '#fff',
            minWidth: '350px',
            backgroundColor: theme?.palette?.TmId.id.toString().includes('9') ? '#26282A' : 'white',
            flexShrink: 0,
          }}
        >
          {reportData?.allAccounts?.length > 10 && (
            <TextField
              variant="outlined"
              required={true}
              size="small"
              InputLabelProps={{ shrink: true }}
              id="cashBook"
              sx={{
                textAlign: 'center',
                justifyContent: 'center',
                width: '95%',
                fontSize: 14,
                height: 40,
                m: 1,
                backgroundColor: theme?.palette?.TmId.id.toString().includes('9') ? '#0b0705' : '#faf5ef',
                borderRadius: '10px',
                color: '#26282A',
                '.MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                  outline: 'none',
                },
                '.MuiInputBase-input ': {
                  fontWeight: 'bold',
                },
                '.MuiSvgIcon-fontSizeMedium ': {
                  color: 'primary.main',
                  fontSize: '30px',
                },
              }}
              placeholder="Search"
              value={searchCashBookText}
              onChange={(e) => setSearchCashBookText(e.target.value)}
            />
          )}
          <Box>
            <Box
              sx={{
                fontWeight: 'bold',
                fontSize: '16px',
              }}
            >
              <ListItem
                sx={{
                  cursor: 'pointer',
                  '&.MuiListItem-root': {
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    overflow: 'hidden',
                    borderBottom: '1px solid #ccc',
                  },
                }}
                secondaryAction={
                  Number(id) !== 7 && (
                    <Typography
                      variant="p"
                      sx={{
                        color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
                      }}
                    >
                      Multi select
                    </Typography>
                  )
                }
              >
                {Number(id) !== 7 && Number(id) !== 9 && (
                  <Typography
                    variant="p"
                    sx={{
                      color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
                    }}
                  >
                    Accounts
                  </Typography>
                )}
              </ListItem>
              <CssBaseline />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* {loading === true ? (
            <ListItem
              sx={{
                cursor: 'pointer',
                '&.MuiListItem-root': {
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  overflow: 'hidden',
                },
              }}
            >
              <ListItemText primary={`Loading...`} />
            </ListItem>
          ) : ( */}
          <Box>
            {Number(id) !== 1 ? (
              ledgerData?.length == 0 ? (
                <ListItem>
                  <ListItemText
                    sx={{
                      whiteSpace: 'wrap', // Optional: Hide overflow text
                      textOverflow: 'ellipsis', // Optional: Show ellipsis for overflow text
                      textAlign: 'center',
                    }}
                    // id={"jjdkefgier"}
                    primary="No Data Found"
                  />
                </ListItem>
              ) : reportData?.allAccounts?.length > 0 ? (
                ledgerData
                  .sort((a, b) => a.accountname.localeCompare(b.accountname))
                  .map((data) => {
                    // For GST Reports (id === 6), only enable account '004'
                    const isGSTReport = Number(id) === 6;
                    const isAccount004 = String(data.accountsid) === '004';
                    const isDisabled = isGSTReport && !isAccount004;

                    return (
                      <ListItem
                        key={data.accountsid}
                        sx={{
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.5 : 1,
                          '&.MuiListItem-root': {
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            alignItems: 'center',
                            overflow: 'hidden',
                            '&:hover': {
                              backgroundColor: isDisabled ? 'transparent' : '#E6CED7',
                            },
                          },
                        }}
                        onClick={(e) => {
                          if (isDisabled) {
                            e.stopPropagation();
                            return;
                          }
                          // Only handle row click if checkbox wasn't clicked
                          const isCheckboxClick =
                            e.target.type === 'checkbox' ||
                            e.target.closest('input[type="checkbox"]') ||
                            e.target.closest('.MuiCheckbox-root') ||
                            e.target.closest('[role="checkbox"]');
                          if (!isCheckboxClick) {
                            e.stopPropagation();
                            handleRowClick(data.accountsid);
                          }
                        }}
                        secondaryAction={
                          id !== '7' &&
                          id !== '9' && (
                            <Checkbox
                              sx={{
                                marginRight: '2px',
                              }}
                              edge="end"
                              disabled={isDisabled}
                              checked={selectedAccounts.some((id) => String(id) === String(data.accountsid))}
                              onChange={(e) => {
                                if (isDisabled) {
                                  e.stopPropagation();
                                  return;
                                }
                                e.stopPropagation();
                                handleCheckboxChange(data.accountsid);
                              }}
                              // onClick={(e) => {
                              //   if (isDisabled) {
                              //     e.stopPropagation();
                              //     return;
                              //   }
                              //   e.stopPropagation();
                              // }}
                            />
                          )
                        }
                      >
                        <ListItemText
                          sx={{
                            whiteSpace: 'wrap', // Optional: Hide overflow text
                            textOverflow: 'ellipsis', // Optional: Show ellipsis for overflow text
                          }}
                          id={data.accountsid}
                          primary={`${data.accountname}`}
                        />
                      </ListItem>
                    );
                  })
              ) : null
            ) : (
              (() => {
                const filteredList =
                  reportData?.allAccounts?.length > 0
                    ? reportData?.allAccounts
                        // .filter((fil) => !restrictedIds.includes(fil.accountsid))
                        .sort((a, b) => a.accountname.localeCompare(b.accountname))
                    : [];
                const cashItems = filteredList.filter((item) => item.accountgroupname === 'Cash in Hand');
                const bankItems = filteredList.filter((item) => item.accountgroupname === 'Bank Accounts');

                const renderAccountItem = (data) => {
                  // For GST Reports (id === 6), only enable account '004'
                  const isGSTReport = Number(id) === 6;
                  const isAccount004 = String(data.accountsid) === '004';
                  const isDisabled = isGSTReport && !isAccount004;

                  return (
                    <ListItem
                      key={data.accountsid}
                      sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.5 : 1,
                        '&.MuiListItem-root': {
                          paddingTop: '0px',
                          paddingBottom: '0px',
                          alignItems: 'center',
                          overflow: 'hidden',
                          '&:hover': {
                            backgroundColor: isDisabled ? 'transparent' : '#E6CED7',
                          },
                        },
                      }}
                      onClick={(e) => {
                        if (isDisabled) {
                          e.stopPropagation();
                          return;
                        }
                        // Only handle row click if checkbox wasn't clicked
                        const isCheckboxClick =
                          e.target.type === 'checkbox' ||
                          e.target.closest('input[type="checkbox"]') ||
                          e.target.closest('.MuiCheckbox-root') ||
                          e.target.closest('[role="checkbox"]');
                        if (!isCheckboxClick) {
                          e.stopPropagation();
                          handleRowClick(data.accountsid);
                        }
                      }}
                      secondaryAction={
                        id !== '7' &&
                        id !== '9' && (
                          <Checkbox
                            sx={{
                              marginRight: '2px',
                            }}
                            edge="end"
                            disabled={isDisabled}
                            checked={selectedAccounts.some((id) => String(id) === String(data.accountsid))}
                            onChange={(e) => {
                              if (isDisabled) {
                                e.stopPropagation();
                                return;
                              }
                              e.stopPropagation();
                              handleCheckboxChange(data.accountsid);
                            }}
                            onClick={(e) => {
                              if (isDisabled) {
                                e.stopPropagation();
                                return;
                              }
                              e.stopPropagation();
                            }}
                          />
                        )
                      }
                    >
                      <ListItemText
                        sx={{
                          whiteSpace: 'wrap', // Optional: Hide overflow text
                          textOverflow: 'ellipsis', // Optional: Show ellipsis for overflow text
                        }}
                        id={data.accountsid}
                        primary={`${data.accountname}`}
                      />
                    </ListItem>
                  );
                };

                return (
                  <>
                    {bankItems.length > 0 && (
                      <Box
                        sx={{
                          border: '1px solid #4CAF50',
                          borderRadius: '4px',
                          margin: '8px',
                          padding: '4px',
                        }}
                      >
                        <Typography
                          variant="p"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '16px',
                            padding: '8px 16px',
                            color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
                            // borderBottom: '1px solid #4CAF50',
                          }}
                        >
                          Bank A/c
                        </Typography>
                        {bankItems.map((data) => renderAccountItem(data))}
                      </Box>
                    )}
                    {cashItems.length > 0 && (
                      <Box
                        sx={{
                          border: '1px solid #2196F3',
                          borderRadius: '4px',
                          margin: '8px',
                          padding: '4px',
                        }}
                      >
                        <Typography
                          variant="p"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '16px',
                            padding: '8px 16px',
                            color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
                            // borderBottom: '1px solid #2196F3',
                          }}
                        >
                          Cash A/c
                        </Typography>
                        {cashItems.map((data) => renderAccountItem(data))}
                      </Box>
                    )}
                  </>
                );
              })()
            )}
          </Box>
          {/* )} */}
        </Box>
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Popover>
    </Box>
  );
};
export default HeaderListPage;
